import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { API_BASE, AUTH_AUDIENCE } from '../config/env';

export type AppLanguage = 'en' | 'es';

export type SubscriptionInvoice = {
    id: string;
    period: string;
    amount: number;
    currency: string;
    status: string;
    issuedAt: string;
    downloadUrl?: string | null;
};

export type SearchResultRule = {
    id: string;
    recordType: string;
    displayName: string;
    fields: string[];
    enabled: boolean;
};

export type CustomDataField = {
    id: string;
    name: string;
    entity: string;
    type: 'text' | 'number' | 'date' | 'select' | 'checkbox';
    required: boolean;
};

export type DataLabel = {
    id: string;
    name: string;
    color: string;
    appliesTo: string;
};

export type ImportantDataField = {
    id: string;
    name: string;
    entity: string;
    reason: string;
};

export type SettingsState = {
    profile: {
        tenantName: string;
        legalName: string;
        logoUrl: string;
        companyId: string;
        phone: string;
        email: string;
        addressLine1: string;
        addressLine2: string;
        city: string;
        state: string;
        postalCode: string;
        country: string;
        formattedAddress: string;
        googlePlaceId: string;
        latitude: string;
        longitude: string;
        source: string;
        taxId: string;
        defaultTaxRate: string;
        registrationNumber: string;
        specialties: string;
        timeZone: string;
        defaultLanguage: AppLanguage;
        enabledLanguages: string;
        domainLanguageDefaults: string;
        defaultCurrency: string;
        website: string;
        medicalDirector: string;
        privacyOfficer: string;
        slogan: string;
        accreditation: string;
        socialMediaAccounts: Record<string, string>;
        letterHeaderTemplates: Array<{ id: string; name: string; content: string; isDefault?: boolean }>;
        letterFooterTemplates: Array<{ id: string; name: string; content: string; isDefault?: boolean }>;
        hqPhotoStorage: {
            enabled: boolean;
            folder: string;
            quality: 'original' | 'optimized';
            retentionDays: number;
        };
        generalSettings: {
            accountOwnerEmail: string;
            ownershipTransferTargetEmail: string;
            ownershipTransferStatus: 'NONE' | 'PENDING' | 'COMPLETED' | 'CANCELLED';
            insuranceBillingEnabled: boolean;
            subscriptionPlan: 'simple' | 'growth' | 'enterprise';
            marketingPlusEnabled: boolean;
            subscriptionCardLabel: string;
            subscriptionInvoices: SubscriptionInvoice[];
            dashboardDataNotes: string;
        };
        dataSettings: {
            searchResultCustomization: SearchResultRule[];
            customFields: CustomDataField[];
            labels: DataLabel[];
            importantFields: ImportantDataField[];
        };
        accountSecurity: {
            twoFactorEnabled: boolean;
            enforceForAdmins: boolean;
            primaryTwoFactorMethod: 'email' | 'authenticator';
            primaryTwoFactorEmail: string;
            lockoutRecoveryEmail: string;
            lockoutThreshold: number;
            passcodeExpiryMinutes: number;
            dataProtectionGuidance: string[];
        };
    };
    locations: Array<{
        id: string;
        name: string;
        address: string;
        contactPhone: string;
        contactEmail: string;
        facilityType: string;
        openingHours: string;
        defaultProviders: string;
        telemedEnabled: boolean;
        primary: boolean;
    }>;
    rbac: {
        invitationEmail: string;
        roles: Array<{ name: string; description: string; permissions: string }>;
    };
    providers: Array<{
        userId: string;
        name: string;
        degrees: string;
        licenseNumber: string;
        specialties: string;
        languages: string;
        telemedAvailable: boolean;
        locations: string;
        signature: string;
    }>;
    scheduling: {
        defaultSlotMinutes: number;
        workingHours: string;
        overbookingAllowed: boolean;
        cancellationPolicy: string;
        noShowPolicy: string;
        defaultView: 'day' | 'week' | 'month';
        showWeekends: boolean;
        showClosedHours: boolean;
        themeMode: 'light' | 'dark';
        weekStart: 'sunday' | 'monday';
        autoAssignRoom: boolean;
        autoAssignAssistant: boolean;
        autoSuggestTimes: boolean;
        predictiveNoShowModel: boolean;
        predictiveSchedulingSuggestions: boolean;
        autofillAvailabilityFromTemplates: boolean;
        autoBlockAfterHighIntensity: boolean;
        aiDistributionBalancing: boolean;
        allowProviderDoubleBooking: boolean;
        allowRoomDoubleBooking: boolean;
        emailReminderTiming: string;
        smsReminderTiming: string;
        twilioMessagingEnabled: boolean;
        twilioFromPhoneNumber: string;
        whatsappMessagingEnabled: boolean;
        whatsappReminderTiming: string;
        whatsappFromPhoneNumber: string;
        reminderMessageTemplates: Record<string, string>;
        voiceReminderEnabled: boolean;
        addToCalendarSupport: boolean;
        telemedLinkDeliveryRules: string;
        cancellationConfirmations: boolean;
        providerDailyScheduleEmail: boolean;
        calendarLockingEnabled: boolean;
        calendarLockPasscode: string;
        calendarPasscodeRules: {
            minLength: number;
            requireNumbers: boolean;
            lockOnIdle: boolean;
            idleTimeoutMinutes: number;
        };
        calendarActivities: Array<{ id: string; title: string; type: string; date: string; owner: string; status: string }>;
        appointmentIconLegend: Array<{ id: string; label: string; color: string; description: string; enabled: boolean }>;
        appointmentOverlapEnabled: boolean;
        calendarAppearance: {
            density: 'comfortable' | 'compact';
            showCancelledAppointments: boolean;
            showAppointmentLegend: boolean;
            colorMode: 'service' | 'provider' | 'status';
        };
        calendarConfiguration: {
            defaultDisplayMode: 'calendar' | 'table';
            visibleHoursStart: number;
            visibleHoursEnd: number;
            showProviderAvatars: boolean;
            showLocationFilter: boolean;
        };
        appointmentSettings: {
            requireService: boolean;
            requireRoom: boolean;
            allowClientCardAccess: boolean;
            defaultStatus: string;
            bookingRuleSummary: string;
        };
        shiftNotes: Array<{ id: string; date: string; providerId?: string | null; providerName: string; note: string }>;
        dayNotes: Array<{ id: string; date: string; note: string }>;
        calendarPresets: Array<{ id: string; name: string; view: 'day' | 'week' | 'month'; displayMode: 'calendar' | 'table'; filterStatus: string; providerIds: string[]; locationIds: string[] }>;
        calendarColumnOrder: {
            providerIds: string[];
            tableColumns: string[];
        };
        calendarFilters: {
            staffIds: string[];
            serviceIds: string[];
            locationIds: string[];
            multiLocationEnabled: boolean;
        };
        externalCalendarSync: Array<{ id: string; provider: string; enabled: boolean; mode: 'one-way' | 'two-way'; accountLabel: string; lastSyncedAt?: string | null }>;
        clinicListSettings: {
            enabled: boolean;
            includeStatus: boolean;
            includeLocation: boolean;
            includeService: boolean;
            defaultOpen: boolean;
        };
    };
    telemed: {
        provider: string;
        apiKey: string;
        region: string;
        meetingUrlPattern: string;
        waitingRoomMax: number;
        waitingRoomAutoJoin: boolean;
        consentText: string;
        quality: string;
        groupVideoEnabled: boolean;
        defaultMaxParticipants: number;
        consultationSupportText: string;
        troubleshootingFaq: string;
    };
};

export const defaultSettings: SettingsState = {
    profile: {
        tenantName: '',
        legalName: '',
        logoUrl: '',
        companyId: '',
        phone: '',
        email: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'US',
        formattedAddress: '',
        googlePlaceId: '',
        latitude: '',
        longitude: '',
        source: 'MANUAL',
        taxId: '',
        defaultTaxRate: '',
        registrationNumber: '',
        specialties: '',
        timeZone: '',
        defaultLanguage: 'en',
        enabledLanguages: 'en, es',
        domainLanguageDefaults: JSON.stringify(
            {
                'app.elen1.com': 'en',
                'mi.elen1.com': 'es',
            },
            null,
            2,
        ),
        defaultCurrency: 'USD',
        website: '',
        medicalDirector: '',
        privacyOfficer: '',
        slogan: '',
        accreditation: '',
        socialMediaAccounts: {
            instagram: '',
            facebook: '',
            linkedIn: '',
            x: '',
            tiktok: '',
            youtube: '',
        },
        letterHeaderTemplates: [
            { id: 'default-header', name: 'Default header', content: '{{companyLogo}}\n{{companyName}}\n{{companyAddress}}', isDefault: true },
        ],
        letterFooterTemplates: [
            { id: 'default-footer', name: 'Default footer', content: 'Confidential clinical communication\n{{companyName}}', isDefault: true },
        ],
        hqPhotoStorage: {
            enabled: true,
            folder: 'Brand / HQ photos',
            quality: 'original',
            retentionDays: 365,
        },
        generalSettings: {
            accountOwnerEmail: '',
            ownershipTransferTargetEmail: '',
            ownershipTransferStatus: 'NONE',
            insuranceBillingEnabled: false,
            subscriptionPlan: 'simple',
            marketingPlusEnabled: false,
            subscriptionCardLabel: 'Visa ending 4242',
            subscriptionInvoices: [
                { id: 'sub-2026-05', period: 'May 2026', amount: 249, currency: 'USD', status: 'Paid', issuedAt: '2026-05-01' },
                { id: 'sub-2026-04', period: 'April 2026', amount: 249, currency: 'USD', status: 'Paid', issuedAt: '2026-04-01' },
                { id: 'sub-2026-03', period: 'March 2026', amount: 249, currency: 'USD', status: 'Paid', issuedAt: '2026-03-01' },
            ],
            dashboardDataNotes: 'Dashboard metrics combine appointment volume, client activity, form completion, billing totals, and outstanding balances for the selected reporting period.',
        },
        dataSettings: {
            searchResultCustomization: [
                { id: 'search-clients', recordType: 'Clients', displayName: 'Clients', fields: ['Name', 'MRN', 'Email', 'Phone'], enabled: true },
                { id: 'search-appointments', recordType: 'Appointments', displayName: 'Appointments', fields: ['Client', 'Provider', 'Service', 'Date'], enabled: true },
                { id: 'search-invoices', recordType: 'Invoices', displayName: 'Invoices', fields: ['Invoice #', 'Client', 'Status', 'Balance'], enabled: true },
                { id: 'search-documents', recordType: 'Documents', displayName: 'Documents', fields: ['Title', 'Folder', 'Uploaded by'], enabled: true },
            ],
            customFields: [
                { id: 'custom-preferred-name', name: 'Preferred name', entity: 'Client', type: 'text', required: false },
                { id: 'custom-referral-source', name: 'Referral source', entity: 'Client', type: 'select', required: false },
                { id: 'custom-treatment-area', name: 'Treatment area', entity: 'Appointment', type: 'text', required: false },
            ],
            labels: [
                { id: 'label-vip', name: 'VIP', color: '#2563eb', appliesTo: 'Clients' },
                { id: 'label-high-risk', name: 'High risk', color: '#dc2626', appliesTo: 'Clients' },
                { id: 'label-insurance-pending', name: 'Insurance pending', color: '#d97706', appliesTo: 'Invoices' },
            ],
            importantFields: [
                { id: 'important-allergies', name: 'Allergies', entity: 'Client', reason: 'Shown prominently during clinical workflows.' },
                { id: 'important-insurance-member-id', name: 'Insurance member ID', entity: 'Client', reason: 'Required before claim submission.' },
                { id: 'important-consent-status', name: 'Consent status', entity: 'Appointment', reason: 'Checked before treatment starts.' },
            ],
        },
        accountSecurity: {
            twoFactorEnabled: false,
            enforceForAdmins: true,
            primaryTwoFactorMethod: 'email',
            primaryTwoFactorEmail: '',
            lockoutRecoveryEmail: '',
            lockoutThreshold: 5,
            passcodeExpiryMinutes: 10,
            dataProtectionGuidance: [
                'Use unique staff accounts and never share login credentials.',
                'Review staff roles and permissions whenever responsibilities change.',
                'Confirm secure links before sharing client documents or forms.',
                'Report missing 2FA passcodes before retrying repeatedly.',
            ],
        },
    },
    locations: [],
    rbac: {
        invitationEmail: '',
        roles: [{ name: 'Admin', description: 'Full access', permissions: 'all' }],
    },
    providers: [],
    scheduling: {
        defaultSlotMinutes: 30,
        workingHours: '',
        overbookingAllowed: false,
        cancellationPolicy: '',
        noShowPolicy: '',
        defaultView: 'week',
        showWeekends: true,
        showClosedHours: true,
        themeMode: 'light',
        weekStart: 'monday',
        autoAssignRoom: false,
        autoAssignAssistant: false,
        autoSuggestTimes: false,
        predictiveNoShowModel: false,
        predictiveSchedulingSuggestions: false,
        autofillAvailabilityFromTemplates: false,
        autoBlockAfterHighIntensity: false,
        aiDistributionBalancing: false,
        allowProviderDoubleBooking: false,
        allowRoomDoubleBooking: false,
        emailReminderTiming: '24h',
        smsReminderTiming: '24h',
        twilioMessagingEnabled: false,
        twilioFromPhoneNumber: '',
        whatsappMessagingEnabled: false,
        whatsappReminderTiming: '24h',
        whatsappFromPhoneNumber: '',
        reminderMessageTemplates: {
            welcome: 'Welcome to {{organizationName}}. Reply to this message or call us if you need help with your care.',
            appointmentConfirmation:
                'Hi {{patientFirstName}}, your {{appointmentType}} appointment at {{organizationName}} is confirmed for {{appointmentTime}} with {{providerName}}. Location: {{locationName}}.',
            appointmentReminder:
                'Reminder: {{patientFirstName}}, you have {{appointmentType}} at {{organizationName}} on {{appointmentTime}} with {{providerName}}. Location: {{locationName}}.',
            appointmentCancellation:
                'Hi {{patientFirstName}}, your {{appointmentType}} appointment at {{organizationName}} on {{appointmentTime}} has been cancelled. Contact us to book another time.',
            appointmentReschedule:
                'Hi {{patientFirstName}}, your {{appointmentType}} appointment at {{organizationName}} has changed. New time: {{appointmentTime}} with {{providerName}}. Location: {{locationName}}.',
            postVisitFollowUp:
                'Thank you for visiting {{organizationName}}, {{patientFirstName}}. Please follow your care instructions and contact us with any questions.',
        },
        voiceReminderEnabled: false,
        addToCalendarSupport: true,
        telemedLinkDeliveryRules: '',
        cancellationConfirmations: true,
        providerDailyScheduleEmail: false,
        calendarLockingEnabled: true,
        calendarLockPasscode: '1234',
        calendarPasscodeRules: {
            minLength: 4,
            requireNumbers: true,
            lockOnIdle: true,
            idleTimeoutMinutes: 15,
        },
        calendarActivities: [
            { id: 'activity-huddle', title: 'Morning clinic huddle', type: 'Meeting', date: '2026-05-19', owner: 'Reception', status: 'Open' },
            { id: 'activity-stock', title: 'Restock treatment rooms', type: 'Task', date: '2026-05-19', owner: 'Nursing', status: 'Open' },
            { id: 'activity-callbacks', title: 'Post-treatment callbacks', type: 'Follow up', date: '2026-05-20', owner: 'Clinical team', status: 'Planned' },
        ],
        appointmentIconLegend: [
            { id: 'icon-arrived', label: 'Checked in or arrived', color: '#10b981', description: 'Client is checked in and ready.', enabled: true },
            { id: 'icon-telemed', label: 'Telemedicine appointment', color: '#0ea5e9', description: 'Appointment has a video visit link.', enabled: true },
            { id: 'icon-pending', label: 'Forms or care tasks pending', color: '#f59e0b', description: 'Required forms, reminders, or care tasks need attention.', enabled: true },
            { id: 'icon-risk', label: 'No-show risk or failed reminders', color: '#ef4444', description: 'Reminder failed or no-show risk is elevated.', enabled: true },
            { id: 'icon-complete', label: 'Cancelled or completed appointment', color: '#94a3b8', description: 'Appointment no longer needs active scheduling work.', enabled: true },
        ],
        appointmentOverlapEnabled: false,
        calendarAppearance: {
            density: 'comfortable',
            showCancelledAppointments: true,
            showAppointmentLegend: false,
            colorMode: 'service',
        },
        calendarConfiguration: {
            defaultDisplayMode: 'calendar',
            visibleHoursStart: 8,
            visibleHoursEnd: 18,
            showProviderAvatars: true,
            showLocationFilter: true,
        },
        appointmentSettings: {
            requireService: true,
            requireRoom: false,
            allowClientCardAccess: true,
            defaultStatus: 'BOOKED',
            bookingRuleSummary: 'Appointments require a client, service, provider, start time, and duration before saving.',
        },
        shiftNotes: [
            { id: 'shift-front-desk', date: '2026-05-19', providerName: 'Front desk', note: 'Confirm arrivals and check medical form completion before rooming.' },
        ],
        dayNotes: [
            { id: 'day-2026-05-19', date: '2026-05-19', note: 'Review high-priority follow-ups during the morning huddle.' },
        ],
        calendarPresets: [
            { id: 'preset-week-all', name: 'Weekly clinic overview', view: 'week', displayMode: 'calendar', filterStatus: 'all', providerIds: [], locationIds: [] },
            { id: 'preset-table-open', name: 'Open appointment table', view: 'day', displayMode: 'table', filterStatus: 'BOOKED', providerIds: [], locationIds: [] },
        ],
        calendarColumnOrder: {
            providerIds: [],
            tableColumns: ['time', 'patient', 'provider', 'service', 'status', 'actions'],
        },
        calendarFilters: {
            staffIds: [],
            serviceIds: [],
            locationIds: [],
            multiLocationEnabled: true,
        },
        externalCalendarSync: [
            { id: 'sync-google', provider: 'Google Calendar', enabled: false, mode: 'two-way', accountLabel: '' },
            { id: 'sync-outlook', provider: 'Outlook Calendar', enabled: false, mode: 'two-way', accountLabel: '' },
            { id: 'sync-ics', provider: 'ICS feed', enabled: false, mode: 'one-way', accountLabel: '' },
        ],
        clinicListSettings: {
            enabled: true,
            includeStatus: true,
            includeLocation: true,
            includeService: true,
            defaultOpen: false,
        },
    },
    telemed: {
        provider: '',
        apiKey: '',
        region: '',
        meetingUrlPattern: '',
        waitingRoomMax: 5,
        waitingRoomAutoJoin: false,
        consentText: '',
        quality: '',
        groupVideoEnabled: true,
        defaultMaxParticipants: 5,
        consultationSupportText: '',
        troubleshootingFaq: '',
    },
};

export function useSettings() {
    const { getAccessTokenSilently } = useAuth0();
    const [settings, setSettings] = useState<SettingsState>(defaultSettings);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = await getAccessTokenSilently({
                authorizationParams: { audience: AUTH_AUDIENCE },
            });
            const [settingsRes, schedulingRes] = await Promise.all([
                fetch(`${API_BASE}/settings`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
                fetch(`${API_BASE}/scheduling-settings`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
            ]);

            if (!settingsRes.ok) throw new Error(`Load failed ${settingsRes.status}`);
            if (!schedulingRes.ok) throw new Error(`Scheduling load failed ${schedulingRes.status}`);

            const data = await settingsRes.json();
            const schedulingData = await schedulingRes.json();
            const rawAddress = data.profile?.address;
            const addressObject = rawAddress && typeof rawAddress === 'object' && !Array.isArray(rawAddress) ? rawAddress as Record<string, unknown> : null;
            const rawDefaultLanguage = String(data.profile?.defaultLanguage ?? 'en').toLowerCase();
            const normalizedDefaultLanguage: AppLanguage =
                rawDefaultLanguage.startsWith('es')
                    ? 'es'
                    : 'en';

            setSettings((prev) => ({
                ...prev,
                profile: {
                    tenantName: data.profile?.tenantName ?? '',
                    legalName: data.profile?.legalName ?? '',
                    logoUrl: data.profile?.logoUrl ?? '',
                    companyId: data.organizationId ?? '',
                    phone: data.profile?.phone ?? '',
                    email: data.profile?.email ?? '',
                    addressLine1: addressObject?.line1 ? String(addressObject.line1) : typeof rawAddress === 'string' ? rawAddress : '',
                    addressLine2: addressObject?.line2 ? String(addressObject.line2) : '',
                    city: addressObject?.city ? String(addressObject.city) : '',
                    state: addressObject?.state ? String(addressObject.state) : '',
                    postalCode: addressObject?.postalCode ? String(addressObject.postalCode) : '',
                    country: addressObject?.country ? String(addressObject.country) : 'US',
                    formattedAddress: addressObject?.formattedAddress ? String(addressObject.formattedAddress) : '',
                    googlePlaceId: addressObject?.googlePlaceId ? String(addressObject.googlePlaceId) : '',
                    latitude: addressObject?.latitude != null ? String(addressObject.latitude) : '',
                    longitude: addressObject?.longitude != null ? String(addressObject.longitude) : '',
                    source: addressObject?.source ? String(addressObject.source) : 'MANUAL',
                    taxId: data.profile?.taxId ?? '',
                    defaultTaxRate:
                        data.profile?.defaultTaxRate == null || data.profile?.defaultTaxRate === ''
                            ? ''
                            : String(data.profile.defaultTaxRate),
                    registrationNumber: data.profile?.registrationNumber ?? '',
                    specialties: (data.profile?.specialties ?? []).join(', '),
                    timeZone: data.profile?.timeZone ?? '',
                    defaultLanguage: normalizedDefaultLanguage,
                    enabledLanguages: (data.profile?.enabledLanguages ?? ['en', 'es']).join(', '),
                    domainLanguageDefaults: data.profile?.domainLanguageDefaults
                        ? JSON.stringify(data.profile.domainLanguageDefaults, null, 2)
                        : JSON.stringify(
                            {
                                'app.elen1.com': 'en',
                                'mi.elen1.com': 'es',
                            },
                            null,
                            2,
                        ),
                    defaultCurrency: data.profile?.defaultCurrency ?? 'USD',
                    website: data.profile?.website ?? '',
                    medicalDirector: data.profile?.medicalDirector ?? '',
                    privacyOfficer: data.profile?.privacyOfficer ?? '',
                    slogan: data.profile?.slogan ?? '',
                    accreditation: data.profile?.accreditation ?? '',
                    socialMediaAccounts: {
                        ...defaultSettings.profile.socialMediaAccounts,
                        ...(data.profile?.socialMediaAccounts && typeof data.profile.socialMediaAccounts === 'object'
                            ? data.profile.socialMediaAccounts
                            : {}),
                    },
                    letterHeaderTemplates: Array.isArray(data.profile?.letterHeaderTemplates) && data.profile.letterHeaderTemplates.length
                        ? data.profile.letterHeaderTemplates
                        : defaultSettings.profile.letterHeaderTemplates,
                    letterFooterTemplates: Array.isArray(data.profile?.letterFooterTemplates) && data.profile.letterFooterTemplates.length
                        ? data.profile.letterFooterTemplates
                        : defaultSettings.profile.letterFooterTemplates,
                    hqPhotoStorage: {
                        ...defaultSettings.profile.hqPhotoStorage,
                        ...(data.profile?.hqPhotoStorage && typeof data.profile.hqPhotoStorage === 'object'
                            ? data.profile.hqPhotoStorage
                            : {}),
                    },
                    generalSettings: {
                        ...defaultSettings.profile.generalSettings,
                        ...(data.profile?.generalSettings && typeof data.profile.generalSettings === 'object'
                            ? data.profile.generalSettings
                            : {}),
                        accountOwnerEmail: data.profile?.generalSettings?.accountOwnerEmail || data.profile?.email || '',
                        subscriptionInvoices: Array.isArray(data.profile?.generalSettings?.subscriptionInvoices) && data.profile.generalSettings.subscriptionInvoices.length
                            ? data.profile.generalSettings.subscriptionInvoices
                            : defaultSettings.profile.generalSettings.subscriptionInvoices,
                    },
                    dataSettings: {
                        ...defaultSettings.profile.dataSettings,
                        ...(data.profile?.dataSettings && typeof data.profile.dataSettings === 'object'
                            ? data.profile.dataSettings
                            : {}),
                        searchResultCustomization: Array.isArray(data.profile?.dataSettings?.searchResultCustomization)
                            ? data.profile.dataSettings.searchResultCustomization
                            : defaultSettings.profile.dataSettings.searchResultCustomization,
                        customFields: Array.isArray(data.profile?.dataSettings?.customFields)
                            ? data.profile.dataSettings.customFields
                            : defaultSettings.profile.dataSettings.customFields,
                        labels: Array.isArray(data.profile?.dataSettings?.labels)
                            ? data.profile.dataSettings.labels
                            : defaultSettings.profile.dataSettings.labels,
                        importantFields: Array.isArray(data.profile?.dataSettings?.importantFields)
                            ? data.profile.dataSettings.importantFields
                            : defaultSettings.profile.dataSettings.importantFields,
                    },
                    accountSecurity: {
                        ...defaultSettings.profile.accountSecurity,
                        ...(data.profile?.accountSecurity && typeof data.profile.accountSecurity === 'object'
                            ? data.profile.accountSecurity
                            : {}),
                    },
                },
                locations:
                    data.locations?.map((l: any) => ({
                        id: l.id ?? '',
                        name: l.name ?? '',
                        address: l.address ?? '',
                        contactPhone: l.contactPhone ?? '',
                        contactEmail: l.contactEmail ?? '',
                        facilityType: l.facilityType ?? '',
                        openingHours: l.openingHours ?? '',
                        defaultProviders: (l.defaultProviders ?? []).join(', '),
                        telemedEnabled: !!l.telemedEnabled,
                        primary: !!l.primary,
                    })) ?? [],
                rbac: {
                    invitationEmail: data.rbac?.invitationEmail ?? '',
                    roles:
                        data.rbac?.roles?.map((r: any) => ({
                            name: r.name ?? '',
                            description: r.description ?? '',
                            permissions: (r.permissions ?? []).join(', '),
                        })) ?? [],
                },
                providers:
                    data.providers?.map((p: any) => ({
                        userId: p.userId ?? '',
                        name: p.name ?? '',
                        degrees: (p.degrees ?? []).join(', '),
                        licenseNumber: p.licenseNumber ?? '',
                        specialties: (p.specialties ?? []).join(', '),
                        languages: (p.languages ?? []).join(', '),
                        telemedAvailable: !!p.telemedAvailable,
                        locations: (p.locations ?? []).join(', '),
                        signature: p.signature ?? '',
                    })) ?? [],
                scheduling: {
                    defaultSlotMinutes: schedulingData?.defaultSlotMinutes ?? defaultSettings.scheduling.defaultSlotMinutes,
                    workingHours: schedulingData?.workingHours ? JSON.stringify(schedulingData.workingHours, null, 2) : '',
                    overbookingAllowed: !!schedulingData?.overbookingAllowed,
                    cancellationPolicy: schedulingData?.cancellationPolicy ?? '',
                    noShowPolicy: schedulingData?.noShowPolicy ?? '',
                    defaultView: schedulingData?.defaultView ?? 'week',
                    showWeekends: schedulingData?.showWeekends ?? true,
                    showClosedHours: schedulingData?.showClosedHours ?? true,
                    themeMode: schedulingData?.themeMode ?? 'light',
                    weekStart: schedulingData?.weekStart ?? 'monday',
                    autoAssignRoom: !!schedulingData?.autoAssignRoom,
                    autoAssignAssistant: !!schedulingData?.autoAssignAssistant,
                    autoSuggestTimes: !!schedulingData?.autoSuggestTimes,
                    predictiveNoShowModel: !!schedulingData?.predictiveNoShowModel,
                    predictiveSchedulingSuggestions: !!schedulingData?.predictiveSchedulingSuggestions,
                    autofillAvailabilityFromTemplates: !!schedulingData?.autofillAvailabilityFromTemplates,
                    autoBlockAfterHighIntensity: !!schedulingData?.autoBlockAfterHighIntensity,
                    aiDistributionBalancing: !!schedulingData?.aiDistributionBalancing,
                    allowProviderDoubleBooking: !!schedulingData?.allowProviderDoubleBooking,
                    allowRoomDoubleBooking: !!schedulingData?.allowRoomDoubleBooking,
                    emailReminderTiming: schedulingData?.emailReminderTiming ?? '',
                    smsReminderTiming: schedulingData?.smsReminderTiming ?? '',
                    twilioMessagingEnabled: !!schedulingData?.twilioMessagingEnabled,
                    twilioFromPhoneNumber: schedulingData?.twilioFromPhoneNumber ?? '',
                    whatsappMessagingEnabled: !!schedulingData?.whatsappMessagingEnabled,
                    whatsappReminderTiming: schedulingData?.whatsappReminderTiming ?? '',
                    whatsappFromPhoneNumber: schedulingData?.whatsappFromPhoneNumber ?? '',
                    reminderMessageTemplates: {
                        ...defaultSettings.scheduling.reminderMessageTemplates,
                        ...(schedulingData?.reminderMessageTemplates && typeof schedulingData.reminderMessageTemplates === 'object'
                            ? schedulingData.reminderMessageTemplates
                            : {}),
                    },
                    voiceReminderEnabled: !!schedulingData?.voiceReminderEnabled,
                    addToCalendarSupport: schedulingData?.addToCalendarSupport ?? true,
                    telemedLinkDeliveryRules: schedulingData?.telemedLinkDeliveryRules ?? '',
                    cancellationConfirmations: schedulingData?.cancellationConfirmations ?? true,
                    providerDailyScheduleEmail: schedulingData?.providerDailyScheduleEmail ?? false,
                    calendarLockingEnabled: schedulingData?.calendarLockingEnabled ?? true,
                    calendarLockPasscode: schedulingData?.calendarLockPasscode ?? defaultSettings.scheduling.calendarLockPasscode,
                    calendarPasscodeRules: {
                        ...defaultSettings.scheduling.calendarPasscodeRules,
                        ...(schedulingData?.calendarPasscodeRules && typeof schedulingData.calendarPasscodeRules === 'object'
                            ? schedulingData.calendarPasscodeRules
                            : {}),
                    },
                    calendarActivities: Array.isArray(schedulingData?.calendarActivities)
                        ? schedulingData.calendarActivities
                        : defaultSettings.scheduling.calendarActivities,
                    appointmentIconLegend: Array.isArray(schedulingData?.appointmentIconLegend)
                        ? schedulingData.appointmentIconLegend
                        : defaultSettings.scheduling.appointmentIconLegend,
                    appointmentOverlapEnabled: schedulingData?.appointmentOverlapEnabled ?? false,
                    calendarAppearance: {
                        ...defaultSettings.scheduling.calendarAppearance,
                        ...(schedulingData?.calendarAppearance && typeof schedulingData.calendarAppearance === 'object'
                            ? schedulingData.calendarAppearance
                            : {}),
                    },
                    calendarConfiguration: {
                        ...defaultSettings.scheduling.calendarConfiguration,
                        ...(schedulingData?.calendarConfiguration && typeof schedulingData.calendarConfiguration === 'object'
                            ? schedulingData.calendarConfiguration
                            : {}),
                    },
                    appointmentSettings: {
                        ...defaultSettings.scheduling.appointmentSettings,
                        ...(schedulingData?.appointmentSettings && typeof schedulingData.appointmentSettings === 'object'
                            ? schedulingData.appointmentSettings
                            : {}),
                    },
                    shiftNotes: Array.isArray(schedulingData?.shiftNotes)
                        ? schedulingData.shiftNotes
                        : defaultSettings.scheduling.shiftNotes,
                    dayNotes: Array.isArray(schedulingData?.dayNotes)
                        ? schedulingData.dayNotes
                        : defaultSettings.scheduling.dayNotes,
                    calendarPresets: Array.isArray(schedulingData?.calendarPresets)
                        ? schedulingData.calendarPresets
                        : defaultSettings.scheduling.calendarPresets,
                    calendarColumnOrder: {
                        ...defaultSettings.scheduling.calendarColumnOrder,
                        ...(schedulingData?.calendarColumnOrder && typeof schedulingData.calendarColumnOrder === 'object'
                            ? schedulingData.calendarColumnOrder
                            : {}),
                    },
                    calendarFilters: {
                        ...defaultSettings.scheduling.calendarFilters,
                        ...(schedulingData?.calendarFilters && typeof schedulingData.calendarFilters === 'object'
                            ? schedulingData.calendarFilters
                            : {}),
                    },
                    externalCalendarSync: Array.isArray(schedulingData?.externalCalendarSync)
                        ? schedulingData.externalCalendarSync
                        : defaultSettings.scheduling.externalCalendarSync,
                    clinicListSettings: {
                        ...defaultSettings.scheduling.clinicListSettings,
                        ...(schedulingData?.clinicListSettings && typeof schedulingData.clinicListSettings === 'object'
                            ? schedulingData.clinicListSettings
                            : {}),
                    },
                },
                telemed: {
                    provider: data.telemed?.provider ?? '',
                    apiKey: data.telemed?.apiKey ?? '',
                    region: data.telemed?.region ?? '',
                    meetingUrlPattern: data.telemed?.meetingUrlPattern ?? '',
                    waitingRoomMax: data.telemed?.waitingRoom?.maxParticipants ?? 5,
                    waitingRoomAutoJoin: data.telemed?.waitingRoom?.autoJoin ?? false,
                    consentText: data.telemed?.consentText ?? '',
                    quality: data.telemed?.quality ?? '',
                    groupVideoEnabled: data.telemed?.groupVideo?.enabled ?? true,
                    defaultMaxParticipants: data.telemed?.groupVideo?.defaultMaxParticipants ?? data.telemed?.waitingRoom?.maxParticipants ?? 5,
                    consultationSupportText: data.telemed?.consultationSupportText ?? '',
                    troubleshootingFaq: data.telemed?.troubleshootingFaq ?? '',
                },
            }));
        } catch (err: any) {
            setError(err.message || 'Failed to load settings');
        } finally {
            setLoading(false);
        }
    }, [getAccessTokenSilently]);

    const save = useCallback(async (newSettings: SettingsState) => {
        const token = await getAccessTokenSilently({
            authorizationParams: { audience: AUTH_AUDIENCE },
        });

        let parsedWorkingHours: any | undefined;
        if (newSettings.scheduling.workingHours) {
            try {
                parsedWorkingHours = JSON.parse(newSettings.scheduling.workingHours);
            } catch (e) {
                console.warn('Invalid working hours JSON, skipping parse');
            }
        }

        const parsedDefaultTaxRate = Number(newSettings.profile.defaultTaxRate);

        const payload = {
            profile: {
                ...newSettings.profile,
                companyId: undefined,
                enabledLanguages: newSettings.profile.enabledLanguages
                    .split(',')
                    .map((s) => s.trim().toLowerCase())
                    .filter(Boolean),
                domainLanguageDefaults: (() => {
                    if (!newSettings.profile.domainLanguageDefaults.trim()) return undefined;
                    try {
                        return JSON.parse(newSettings.profile.domainLanguageDefaults);
                    } catch {
                        return undefined;
                    }
                })(),
                specialties: newSettings.profile.specialties
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean),
                defaultTaxRate:
                    newSettings.profile.defaultTaxRate.trim() === ''
                        ? undefined
                        : Number.isFinite(parsedDefaultTaxRate)
                            ? Math.max(0, parsedDefaultTaxRate)
                            : undefined,
                logoUrl: newSettings.profile.logoUrl || null,
                website: newSettings.profile.website || null,
                address: {
                    line1: newSettings.profile.addressLine1,
                    line2: newSettings.profile.addressLine2 || null,
                    city: newSettings.profile.city,
                    state: newSettings.profile.state,
                    postalCode: newSettings.profile.postalCode,
                    country: newSettings.profile.country || 'US',
                    formattedAddress: newSettings.profile.formattedAddress || null,
                    googlePlaceId: newSettings.profile.googlePlaceId || null,
                    latitude: newSettings.profile.latitude ? Number(newSettings.profile.latitude) : undefined,
                    longitude: newSettings.profile.longitude ? Number(newSettings.profile.longitude) : undefined,
                    source: newSettings.profile.source || 'MANUAL',
                },
            },
            locations: newSettings.locations.map((l) => ({
                ...l,
                defaultProviders: l.defaultProviders
                    .split(',')
                    .map((p) => p.trim())
                    .filter(Boolean),
            })),
            rbac: {
                invitationEmail: newSettings.rbac.invitationEmail || undefined,
                roles: newSettings.rbac.roles.map((r) => ({
                    ...r,
                    permissions: r.permissions
                        .split(',')
                        .map((p) => p.trim())
                        .filter(Boolean),
                })),
            },
            providers: newSettings.providers.map((p) => ({
                ...p,
                degrees: p.degrees.split(',').map((v) => v.trim()).filter(Boolean),
                specialties: p.specialties.split(',').map((v) => v.trim()).filter(Boolean),
                languages: p.languages.split(',').map((v) => v.trim()).filter(Boolean),
                locations: p.locations.split(',').map((v) => v.trim()).filter(Boolean),
            })),
            telemed: {
                provider: newSettings.telemed.provider,
                apiKey: newSettings.telemed.apiKey,
                region: newSettings.telemed.region,
                meetingUrlPattern: newSettings.telemed.meetingUrlPattern,
                waitingRoom: {
                    maxParticipants: newSettings.telemed.waitingRoomMax,
                    autoJoin: newSettings.telemed.waitingRoomAutoJoin,
                },
                consentText: newSettings.telemed.consentText,
                quality: newSettings.telemed.quality,
                groupVideo: {
                    enabled: newSettings.telemed.groupVideoEnabled,
                    defaultMaxParticipants: newSettings.telemed.defaultMaxParticipants,
                },
                consultationSupportText: newSettings.telemed.consultationSupportText,
                troubleshootingFaq: newSettings.telemed.troubleshootingFaq,
            },
        };

        const schedulingPayload = {
            defaultSlotMinutes: newSettings.scheduling.defaultSlotMinutes,
            workingHours: parsedWorkingHours,
            overbookingAllowed: newSettings.scheduling.overbookingAllowed,
            cancellationPolicy: newSettings.scheduling.cancellationPolicy,
            noShowPolicy: newSettings.scheduling.noShowPolicy,
            defaultView: newSettings.scheduling.defaultView,
            showWeekends: newSettings.scheduling.showWeekends,
            showClosedHours: newSettings.scheduling.showClosedHours,
            themeMode: newSettings.scheduling.themeMode,
            weekStart: newSettings.scheduling.weekStart,
            autoAssignRoom: newSettings.scheduling.autoAssignRoom,
            autoAssignAssistant: newSettings.scheduling.autoAssignAssistant,
            autoSuggestTimes: newSettings.scheduling.autoSuggestTimes,
            predictiveNoShowModel: newSettings.scheduling.predictiveNoShowModel,
            predictiveSchedulingSuggestions: newSettings.scheduling.predictiveSchedulingSuggestions,
            autofillAvailabilityFromTemplates: newSettings.scheduling.autofillAvailabilityFromTemplates,
            autoBlockAfterHighIntensity: newSettings.scheduling.autoBlockAfterHighIntensity,
            aiDistributionBalancing: newSettings.scheduling.aiDistributionBalancing,
            allowProviderDoubleBooking: newSettings.scheduling.allowProviderDoubleBooking,
            allowRoomDoubleBooking: newSettings.scheduling.allowRoomDoubleBooking,
            emailReminderTiming: newSettings.scheduling.emailReminderTiming,
            smsReminderTiming: newSettings.scheduling.smsReminderTiming,
            twilioMessagingEnabled: newSettings.scheduling.twilioMessagingEnabled,
            twilioFromPhoneNumber: newSettings.scheduling.twilioFromPhoneNumber || null,
            whatsappMessagingEnabled: newSettings.scheduling.whatsappMessagingEnabled,
            whatsappReminderTiming: newSettings.scheduling.whatsappReminderTiming,
            whatsappFromPhoneNumber: newSettings.scheduling.whatsappFromPhoneNumber || null,
            reminderMessageTemplates: newSettings.scheduling.reminderMessageTemplates,
            voiceReminderEnabled: newSettings.scheduling.voiceReminderEnabled,
            addToCalendarSupport: newSettings.scheduling.addToCalendarSupport,
            telemedLinkDeliveryRules: newSettings.scheduling.telemedLinkDeliveryRules,
            cancellationConfirmations: newSettings.scheduling.cancellationConfirmations,
            providerDailyScheduleEmail: newSettings.scheduling.providerDailyScheduleEmail,
            calendarLockingEnabled: newSettings.scheduling.calendarLockingEnabled,
            calendarLockPasscode: newSettings.scheduling.calendarLockPasscode || null,
            calendarPasscodeRules: newSettings.scheduling.calendarPasscodeRules,
            calendarActivities: newSettings.scheduling.calendarActivities,
            appointmentIconLegend: newSettings.scheduling.appointmentIconLegend,
            appointmentOverlapEnabled: newSettings.scheduling.appointmentOverlapEnabled,
            calendarAppearance: newSettings.scheduling.calendarAppearance,
            calendarConfiguration: newSettings.scheduling.calendarConfiguration,
            appointmentSettings: newSettings.scheduling.appointmentSettings,
            shiftNotes: newSettings.scheduling.shiftNotes,
            dayNotes: newSettings.scheduling.dayNotes,
            calendarPresets: newSettings.scheduling.calendarPresets,
            calendarColumnOrder: newSettings.scheduling.calendarColumnOrder,
            calendarFilters: newSettings.scheduling.calendarFilters,
            externalCalendarSync: newSettings.scheduling.externalCalendarSync,
            clinicListSettings: newSettings.scheduling.clinicListSettings,
        };

        const [settingsRes, schedulingRes] = await Promise.all([
            fetch(`${API_BASE}/settings`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            }),
            fetch(`${API_BASE}/scheduling-settings`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(schedulingPayload),
            }),
        ]);

        if (!settingsRes.ok) throw new Error(`Save failed ${settingsRes.status}`);
        if (!schedulingRes.ok) throw new Error(`Scheduling save failed ${schedulingRes.status}`);
    }, [getAccessTokenSilently]);

    return { settings, setSettings, load, save, loading, error };
}
